/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import MobileFormDialog from "../../components/ui/mobile-form-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Plus, Trash2, Shield, CheckCircle, Clock, AlertTriangle, ChevronRight,
} from "lucide-react";


const COVERAGES = [
  { value: "responsabilidade_civil", label: "Responsabilidade Civil" },
  { value: "casco", label: "Casco" },
  { value: "multi_risco", label: "Multi-Risco" },
  { value: "outro", label: "Outro" },
];

const FREQS = [
  { value: "anual", label: "Anual (1 prestação)" },
  { value: "semestral", label: "Semestral (2)" },
  { value: "trimestral", label: "Trimestral (4)" },
  { value: "mensal", label: "Mensal (12)" },
];

function eur(cents) {
  if (cents == null) return "—";
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);
}


export default function InsurancesDialog({ aircraftId, registration, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/di/aircraft/${aircraftId}/insurances`, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar seguros");
    } finally {
      setLoading(false);
    }
  }, [aircraftId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (ins) => {
    if (!window.confirm(`Eliminar apólice ${ins.policy_number}? Todas as prestações associadas serão apagadas.`)) return;
    try {
      await axios.delete(`${API}/di/insurances/${ins.id}`, { withCredentials: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const markPaid = async (insId, paymentId) => {
    try {
      await axios.patch(
        `${API}/di/insurances/${insId}/payments/${paymentId}`,
        { paid_at: new Date().toISOString().slice(0, 10) },
        { withCredentials: true },
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const revertPaid = async (insId, paymentId) => {
    if (!window.confirm("Desmarcar como pago?")) return;
    try {
      await axios.patch(
        `${API}/di/insurances/${insId}/payments/${paymentId}`,
        { paid_at: null },
        { withCredentials: true },
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="di-ins-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-400" /> Seguros · {registration}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Apólices ligadas a esta aeronave. Prestações geradas automaticamente conforme a periodicidade.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between mb-2 text-xs">
          <div className="text-slate-400">{items.length} apólice(s)</div>
          <Button size="sm" onClick={() => { setEditing(null); setCreating(true); }} className="btn-cockpit" data-testid="di-ins-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Nova apólice
          </Button>
        </div>

        {loading ? (
          <div className="py-8 text-center text-slate-500 text-xs">A carregar…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-xs border border-dashed border-cockpit-border rounded">
            Sem seguros registados nesta aeronave.
          </div>
        ) : (
          <div className="space-y-3" data-testid="di-ins-list">
            {items.map((ins) => (
              <InsuranceCard
                key={ins.id}
                insurance={ins}
                onEdit={() => { setEditing(ins); setCreating(false); }}
                onDelete={() => remove(ins)}
                onPay={(pid) => markPaid(ins.id, pid)}
                onRevert={(pid) => revertPaid(ins.id, pid)}
              />
            ))}
          </div>
        )}

        {(creating || editing) && (
          <InsuranceForm
            aircraftId={aircraftId}
            insurance={editing}
            onClose={() => { setCreating(false); setEditing(null); }}
            onSaved={() => { setCreating(false); setEditing(null); load(); }}
          />
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InsuranceCard({ insurance, onEdit, onDelete, onPay, onRevert }) {
  const s = insurance.summary || {};
  const headerCls = s.expired
    ? "border-red-500/40 bg-red-500/5"
    : s.overdue_count > 0
      ? "border-amber-500/40 bg-amber-500/5"
      : "border-emerald-500/30 bg-emerald-500/5";

  return (
    <div className={`border ${headerCls} rounded p-3`} data-testid="di-ins-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
            <h4 className="font-semibold text-slate-100 truncate">{insurance.insurer}</h4>
            <span className="text-[10px] uppercase px-1 rounded bg-slate-800 text-slate-400">
              {insurance.coverage_type}
            </span>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            Apólice {insurance.policy_number} · {insurance.policy_start} → {insurance.policy_end}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-orange-300" title="Editar">
            <Plus className="w-3.5 h-3.5 rotate-45" />
          </button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400" title="Eliminar">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[10px]">
        <Chip label="Prémio anual" value={eur(s.annual_premium_cents)} />
        <Chip label="Validade" value={
          s.expired ? `Expirou há ${-s.days_to_expiry}d` :
          s.days_to_expiry != null ? `${s.days_to_expiry}d restantes` : "—"
        } accent={s.expired ? "red" : (s.days_to_expiry != null && s.days_to_expiry <= 30 ? "amber" : null)} />
        <Chip label="Já pago" value={eur(s.paid_cents)} accent="emerald" />
        <Chip
          label={s.overdue_count > 0 ? "Em atraso" : "Em dívida"}
          value={s.overdue_count > 0 ? `${eur(s.overdue_cents)} (${s.overdue_count})` : eur(s.due_cents)}
          accent={s.overdue_count > 0 ? "red" : null}
        />
      </div>

      {/* Payments table */}
      {insurance.payments?.length > 0 && (
        <details className="mt-2 group" open={s.overdue_count > 0}>
          <summary className="cursor-pointer text-[11px] text-slate-400 hover:text-slate-200">
            {insurance.payments.length} prestação(ões)
          </summary>
          <div className="mt-1 border border-cockpit-border rounded overflow-hidden">
            <table className="w-full text-[11px]" data-testid="di-ins-payments-table">
              <thead className="bg-slate-800 text-slate-400 text-[10px]">
                <tr>
                  <th className="text-left p-1.5">#</th>
                  <th className="text-left p-1.5">Vencimento</th>
                  <th className="text-right p-1.5">Valor</th>
                  <th className="text-left p-1.5">Estado</th>
                  <th className="text-right p-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {insurance.payments.map((p) => (
                  <tr key={p.id} className="border-t border-slate-800">
                    <td className="p-1.5 text-slate-500">#{p.installment_idx}</td>
                    <td className="p-1.5 font-mono">{p.due_date}</td>
                    <td className="p-1.5 font-mono text-right">{eur(p.amount_cents)}</td>
                    <td className="p-1.5">
                      <PaymentStatus status={p.status} paidAt={p.paid_at} />
                    </td>
                    <td className="p-1.5 text-right">
                      {p.status === "paga" ? (
                        <button onClick={() => onRevert(p.id)} className="text-[10px] text-slate-400 hover:text-amber-300" data-testid="di-ins-pay-revert">
                          desfazer
                        </button>
                      ) : (
                        <button onClick={() => onPay(p.id)} className="text-[10px] text-emerald-400 hover:text-emerald-300 font-semibold" data-testid="di-ins-pay-mark">
                          marcar pago
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {insurance.notes && (
        <details className="mt-2 group">
          <summary className="cursor-pointer text-[10px] text-slate-400 hover:text-slate-200 select-none inline-flex items-center gap-1">
            <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
            Notas
          </summary>
          <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] text-slate-300 bg-slate-950/50 border border-slate-800 rounded p-2">{insurance.notes}</pre>
        </details>
      )}
    </div>
  );
}

function Chip({ label, value, accent }) {
  const cls = accent === "red" ? "border-red-500/40 bg-red-500/5 text-red-200"
    : accent === "amber" ? "border-amber-500/40 bg-amber-500/5 text-amber-200"
    : accent === "emerald" ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
    : "border-slate-700 bg-slate-800/40 text-slate-300";
  return (
    <div className={`px-2 py-1 rounded border ${cls}`}>
      <div className="text-[9px] uppercase opacity-70">{label}</div>
      <div className="font-mono text-[11px]">{value}</div>
    </div>
  );
}

function PaymentStatus({ status, paidAt }) {
  if (status === "paga") {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-300">
        <CheckCircle className="w-3 h-3" /> Paga {paidAt && `· ${paidAt}`}
      </span>
    );
  }
  if (status === "atrasada") {
    return (
      <span className="inline-flex items-center gap-1 text-red-300">
        <AlertTriangle className="w-3 h-3" /> Atrasada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-slate-400">
      <Clock className="w-3 h-3" /> Futura
    </span>
  );
}


function _plusOneYear(isoDate) {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function InsuranceForm({ aircraftId, insurance, onClose, onSaved }) {
  const isEdit = !!insurance?.id;
  const _defaultStart = insurance?.policy_start || new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(() => ({
    policy_number: insurance?.policy_number || "",
    insurer: insurance?.insurer || "",
    coverage_type: insurance?.coverage_type || "responsabilidade_civil",
    policy_start: _defaultStart,
    policy_end: insurance?.policy_end || (isEdit ? "" : _plusOneYear(_defaultStart)),
    annual_premium_eur: insurance ? ((insurance.annual_premium_cents || 0) / 100).toFixed(2) : "",
    payment_frequency: insurance?.payment_frequency || "anual",
    notes: insurance?.notes || "",
    regenerate_payments: false,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!form.policy_number.trim() || !form.insurer.trim() || !form.policy_end) {
      setError("Apólice, seguradora e fim são obrigatórios.");
      return;
    }
    const cents = Math.round(parseFloat(form.annual_premium_eur || "0") * 100);
    if (Number.isNaN(cents) || cents < 0) {
      setError("Prémio anual inválido.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        policy_number: form.policy_number.trim(),
        insurer: form.insurer.trim(),
        coverage_type: form.coverage_type,
        policy_start: form.policy_start,
        policy_end: form.policy_end,
        annual_premium_cents: cents,
        payment_frequency: form.payment_frequency,
        notes: form.notes || "",
      };
      if (isEdit) {
        await axios.put(`${API}/di/insurances/${insurance.id}`, { ...payload, regenerate_payments: form.regenerate_payments }, { withCredentials: true });
        toast.success("Apólice atualizada");
      } else {
        await axios.post(`${API}/di/aircraft/${aircraftId}/insurances`, payload, { withCredentials: true });
        toast.success("Apólice criada");
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileFormDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Editar apólice ${insurance.policy_number}` : "Nova apólice"}
      description="As prestações são geradas automaticamente conforme a periodicidade selecionada."
      size="md"
      onSubmit={submit}
      submitLabel={isEdit ? "Atualizar" : "Criar"}
      submitting={saving}
      testId="di-ins-form-dialog"
    >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div className="sm:col-span-2">
            <Label className="text-slate-300">Seguradora *</Label>
            <Input
              value={form.insurer}
              onChange={(e) => setForm({ ...form, insurer: e.target.value })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-ins-form-insurer"
            />
          </div>
          <div>
            <Label className="text-slate-300">Nº apólice *</Label>
            <Input
              value={form.policy_number}
              onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-ins-form-policy"
            />
          </div>
          <div>
            <Label className="text-slate-300">Cobertura</Label>
            <Select value={form.coverage_type} onValueChange={(v) => setForm({ ...form, coverage_type: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {COVERAGES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Início *</Label>
            <Input
              type="date"
              value={form.policy_start}
              onChange={(e) => {
                const newStart = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  policy_start: newStart,
                  // Auto-suggest end = start + 1 year (only if user hasn't set an end yet OR end <= new start)
                  policy_end: (!prev.policy_end || prev.policy_end <= newStart) ? _plusOneYear(newStart) : prev.policy_end,
                }));
              }}
              className="bg-slate-800 border-slate-700"
              data-testid="di-ins-form-start"
            />
          </div>
          <div>
            <Label className="text-slate-300">Fim *</Label>
            <Input
              type="date"
              value={form.policy_end}
              onChange={(e) => setForm({ ...form, policy_end: e.target.value })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-ins-form-end"
            />
          </div>
          <div>
            <Label className="text-slate-300">Prémio anual (€) *</Label>
            <Input
              type="number" min={0} step={0.01}
              value={form.annual_premium_eur}
              onChange={(e) => setForm({ ...form, annual_premium_eur: e.target.value })}
              placeholder="0.00"
              className="bg-slate-800 border-slate-700"
              data-testid="di-ins-form-premium"
            />
          </div>
          <div>
            <Label className="text-slate-300">Periodicidade</Label>
            <Select value={form.payment_frequency} onValueChange={(v) => setForm({ ...form, payment_frequency: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="di-ins-form-freq"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {FREQS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-slate-300">Notas</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          {isEdit && (
            <label className="sm:col-span-2 flex items-center gap-2 text-[11px] text-amber-300 cursor-pointer">
              <input
                type="checkbox"
                checked={form.regenerate_payments}
                onChange={(e) => setForm({ ...form, regenerate_payments: e.target.checked })}
                data-testid="di-ins-form-regen"
              />
              Regenerar prestações ainda não pagas (necessário se mudaste periodicidade ou prémio)
            </label>
          )}
          {error && <div className="sm:col-span-2 text-red-400">{error}</div>}
        </div>
    </MobileFormDialog>
  );
}
